import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import ReactDOMServer from "react-dom/server";
import L from "leaflet";
import {Spin, message} from "antd";
import {
    ArrowsAltOutlined,
    ShrinkOutlined,
    InboxOutlined,
    EnvironmentOutlined,
    CloseOutlined,
    CheckOutlined
} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "utils/helpers";
import {courierStatusColors, pickupColor, cities} from "constants/constants";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {SubcompanyMarker} from "components/_defaultComponents/SubcompanyMarker/SubcompanyMarker";
import {TileLayerControl} from "components/_defaultComponents/TileLayerControl";
import {ConditionalTooltip} from "components/_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CourierStatsBox} from "./CourierStatsBox";
import {LiveOperationMapLayerSwitches} from "./LiveOperationMapLayerSwitches";
import {MapCourierPopup} from "./popups/MapCourierPopup";
import {MapSubCompanyPopup} from "./popups/MapSubCompanyPopup";
import {MapOrderPopup} from "./popups/MapOrderPopup";
import {
    MapOuter,
    MapDiv,
    MapLoadingOverlay,
    ExpandButton,
    ExternalPopup,
    PopupContent,
    CloseButton,
    FocusBadge,
    FocusOrderNo,
    FocusOrderNoCopyTick,
    FocusClearButton
} from "./LiveOperationMap.styles";
import {resolveStatusKey, SYNTHETIC_STATUS_FILTERS} from "../utils/orderGrouping";

const buildCircleIcon = ({html, size = 32}) =>
    L.divIcon({
        html,
        className: "custom-marker-icon",
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2]
    });

const renderColoredIcon = (color, child) =>
    ReactDOMServer.renderToString(
        <div
            style={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: color,
                borderRadius: "50%",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                border: "2px solid #fff"
            }}
        >
            {child}
        </div>
    );

// Popup'ın pin'e olan dikey mesafesi ve harita kenarlarına bırakılacak boşluk
const POPUP_PIN_OFFSET = 16;
const POPUP_EDGE_MARGIN = 8;

const ORDER_PIN_JITTER_DEGREES = 0.00025;

// Haritanın en uzak (en küçük) zoom seviyesi: hem kullanıcının elle uzaklaştırabileceği taban
// hem de fitBounds'un pinler dağınıkken ulaşabileceği taban — ikisi kasıtlı olarak aynı sabitten
// besleniyor.
const MIN_ZOOM = 6.8;

const applyOrderPinJitter = pins => {
    const groups = new Map();
    pins.forEach(pin => {
        const key = `${pin.lat.toFixed(6)},${pin.lng.toFixed(6)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(pin);
    });
    groups.forEach(group => {
        if (group.length <= 1) return;
        group.forEach((pin, index) => {
            const angle = (2 * Math.PI * index) / group.length;
            pin.lat += ORDER_PIN_JITTER_DEGREES * Math.sin(angle);
            pin.lng += ORDER_PIN_JITTER_DEGREES * Math.cos(angle);
        });
    });
    return pins;
};

const buildOrderPins = (
    deliveries,
    courierSearchText = "",
    statusFilter = "all",
    shiftCourierIds = null
) => {
    const needle = courierSearchText.trim().toLocaleLowerCase("tr-TR");
    const seen = new Set();
    const pins = [];
    (deliveries || []).forEach(delivery => {
        if (statusFilter && statusFilter !== "all") {
            // "queue"/"forceAssigned" bu ekrana özgü sentetik durumlardır (orderGrouping.js'teki
            // resolveStatusKey ile türetilir); somut durumlar (ör. onTheWayForPickUp) ise ham
            // delivery.deliveryState alanıyla eşleşir — tablodaki matchesFilter ile aynı mantık,
            // aksi halde harita hiç eşleşmeyip pinsiz kalıyordu.
            const courierId = delivery.assignedCourier?._id || null;
            const matchesFilter = SYNTHETIC_STATUS_FILTERS.has(statusFilter)
                ? resolveStatusKey(delivery, courierId) === statusFilter
                : delivery.deliveryState === statusFilter;
            if (!matchesFilter) return;
        }
        if (shiftCourierIds) {
            const courierId = delivery.assignedCourier?._id;
            if (!courierId || !shiftCourierIds.has(String(courierId))) return;
        }
        if (needle) {
            const courierName = (delivery.assignedCourier?.fullname || "").toLocaleLowerCase(
                "tr-TR"
            );
            if (!courierName.includes(needle)) return;
        }
        const orders = delivery.orders?.length ? delivery.orders : [{}];
        orders.forEach((order, index) => {
            const receiverLat = order.receiverLocation?.lat;
            const receiverLng = order.receiverLocation?.lon;
            if (!receiverLat || !receiverLng) return;
            const pickup =
                delivery.pickupInfos?.find(p => p._id === order.pickupInfoId) ||
                delivery.pickupInfos?.[0];
            const dedupeKey = `${delivery._id}-${order._id || order.orderNumber || index}`;
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);
            pins.push({
                delivery,
                order,
                pickup,
                lat: receiverLat,
                lng: receiverLng
            });
        });
    });
    return applyOrderPinJitter(pins);
};

export const LiveOperationMap = observer(
    ({
        filters,
        options,
        onOptionsChange,
        mapRecall,
        courierList = [],
        courierStats,
        queueDeliveries,
        currentDeliveries,
        courierSearchText = "",
        orderStatusFilter = "all",
        shiftCourierIds = null,
        filterSignature = "",
        loading = false,
        focusedTarget = null,
        onClearFocus
    }) => {
        const stores = useStore();
        const [mapMode, setMapMode] = useState("relative");
        const authCities = stores.meStore.auth?.cities;
        const cityView = useMemo(() => {
            // "Tümü" seçiliyken tek bir şehir merkezi yok; yetkili ilk şehre kurulur ki kısıtlı
            // kullanıcı haritayı alakasız bir şehirde açmasın. Pinler geldiğinde filterSignature
            // tetikli fitBounds görüntüyü zaten oturtuyor — burası yalnızca ilk render merkezi.
            const cityValue =
                filters.city === "all" ? authCities?.[0] || filters.city : filters.city;
            const match = cities.find(c => c.value === cityValue);
            if (match?.center) return {center: match.center, zoom: match.zoom || 11};
            return {center: {lat: 41.0082, lng: 28.9784}, zoom: 11}; // İstanbul varsayılanı
        }, [filters.city, authCities]);
        const [center] = useState(cityView.center);
        const [zoom] = useState(cityView.zoom);

        const [selectedCourier, setSelectedCourier] = useState(null);
        const [selectedSubCompany, setSelectedSubCompany] = useState(null);
        const [selectedOrder, setSelectedOrder] = useState(null);
        const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
        const [popupOffset, setPopupOffset] = useState({left: 0, top: 0});

        const [orderNumberCopied, setOrderNumberCopied] = useState(false);

        const [courierInfo, setCourierInfo] = useState(null);
        const [courierInfoLoading, setCourierInfoLoading] = useState(false);
        const subCompanyDetailCache = useRef(new Map());
        const [subCompanyDetail, setSubCompanyDetail] = useState(null);
        const [subCompanyDetailLoading, setSubCompanyDetailLoading] = useState(false);

        const mapRef = useRef(null);
        const mapInstanceRef = useRef(null);
        const tileLayerRef = useRef(null);
        const popupRef = useRef(null);
        const courierLayerRef = useRef(null);
        const subCompanyLayerRef = useRef(null);
        const orderLayerRef = useRef(null);
        const lastFitSignatureRef = useRef(null);
        const lastFocusKeyRef = useRef(null);

        const dedicatedCompanyId = filters.company === "pool" ? "" : filters.company;

        // Şube pinleri filtre değişiminde bir an eski kapsamıyla duruyordu. Yanıt, alındığı
        // filtre anahtarıyla birlikte saklanır; böylece "elimdeki veri güncel filtreye mi ait"
        // sorusu RENDER sırasında yanıtlanır. Effect ile sıfırlamak yetmiyordu: fitBounds
        // effect'i aynı commit'te hâlâ eski listeyi görüp ona göre zoom yapıyordu.
        const subCompanyFilterKey = JSON.stringify([
            filters.city,
            dedicatedCompanyId,
            filters.zoneManagementId,
            filters.zoneId
        ]);

        const [, subCompanyMapData] = useFetch({
            defaultDataValue: {rows: [], filterKey: null},
            // Anahtar transform yerine fetchFn içinde iliştiriliyor: useFetch'in refetch yolu
            // transform'u uygulamıyor (hooks/useFetch.js), o yoldan gelen veri anahtarsız kalır
            // ve ekran boşalırdı.
            fetchFn: async () => {
                const rows = await stores.companyStore.getSubCompanyMapList({
                    companyId: dedicatedCompanyId,
                    disabled: "false",
                    city: filters.city,
                    zoneManagementId: filters.zoneManagementId,
                    zoneId: filters.zoneId
                });
                return {rows: rows || [], filterKey: subCompanyFilterKey};
            },
            dependencies: [
                filters.city,
                dedicatedCompanyId,
                filters.zoneManagementId,
                filters.zoneId,
                mapRecall
            ],
            condition: Boolean(filters.city)
        });

        const subCompanyIsPending = subCompanyMapData?.filterKey !== subCompanyFilterKey;
        const subCompanyMapList = subCompanyIsPending ? [] : subCompanyMapData.rows;
        // Sayfadan gelen kurye/sipariş yüklemesi + buradaki şube yüklemesi tek göstergede.
        const showLoadingOverlay = loading || subCompanyIsPending;

        const [, poolListData] = useFetch({
            defaultDataValue: [],
            fetchFn: stores.poolStore.getPoolList.bind(null, {
                limit: 1000,
                page: 1,
                city: filters.city
            }),
            dependencies: [filters.city, JSON.stringify(filters.pool)],
            condition: Boolean(filters.pool?.length)
        });

        const poolSubCompanyIds = useMemo(() => {
            if (!filters.pool?.length) return null;
            const selectedPools = (poolListData?.data || []).filter(pool =>
                filters.pool.includes(pool._id)
            );
            return new Set(
                selectedPools.flatMap(pool => (pool.subCompanies || []).map(sc => String(sc._id)))
            );
        }, [poolListData, filters.pool]);

        const orderPins = useMemo(
            () =>
                buildOrderPins(
                    [...(queueDeliveries || []), ...(currentDeliveries || [])],
                    courierSearchText,
                    orderStatusFilter,
                    shiftCourierIds
                ),
            [
                queueDeliveries,
                currentDeliveries,
                courierSearchText,
                orderStatusFilter,
                shiftCourierIds
            ]
        );

        // Odaktaki sipariş pini, tablonun beslendiği aynı filtreli listede aranır — jitter
        // uygulanmış koordinatı kullanmak zorunlu, aksi halde zoom pinin bastığı noktadan kayar.
        const focusedOrderPin = useMemo(() => {
            if (focusedTarget?.type !== "order") return null;
            return (
                orderPins.find(
                    pin =>
                        String(pin.delivery?._id) === String(focusedTarget.deliveryId) &&
                        (!focusedTarget.orderId ||
                            String(pin.order?._id) === String(focusedTarget.orderId))
                ) || null
            );
        }, [orderPins, focusedTarget]);

        // Kurye odağında kuryenin taşıdığı TÜM sipariş pinleri — aynı filtreli listeden (jitter
        // uygulanmış koordinatlarla, tek sipariş odağıyla aynı gerekçe).
        const focusedCourierPins = useMemo(() => {
            if (focusedTarget?.type !== "courier") return [];
            return orderPins.filter(
                pin =>
                    String(pin.delivery?.assignedCourier?._id || "") ===
                    String(focusedTarget.courierId)
            );
        }, [orderPins, focusedTarget]);

        // Odak tipinden bağımsız, haritada gösterilecek sipariş pinlerinin ortak listesi.
        const focusedPins =
            focusedTarget?.type === "order" ? [focusedOrderPin] : focusedCourierPins;

        // Şube pinleri ham (filtrelenmemiş) maplist'ten çözülür: aktif havuz/şube filtresi listeyi
        // daraltmış olsa bile odaklanan sipariş(ler)in şubesi gösterilmeli. Kurye odağında birden
        // fazla şube olabilir (kurye farklı şubelerden sipariş taşıyor olabilir).
        const focusedSubCompanies = useMemo(() => {
            const pickupIds = Array.from(
                new Set(
                    focusedPins
                        .map(pin => pin?.pickup?._id)
                        .filter(Boolean)
                        .map(String)
                )
            );
            if (!pickupIds.length) return [];
            return (subCompanyMapList || []).filter(sc => pickupIds.includes(String(sc._id)));
        }, [subCompanyMapList, focusedPins]);

        const focusedCourier = useMemo(() => {
            const courierId =
                focusedTarget?.type === "courier"
                    ? focusedTarget.courierId
                    : focusedOrderPin?.delivery?.assignedCourier?._id;
            if (!courierId) return null;
            return (
                courierList.find(
                    cs =>
                        String(cs.courier?._id || "") === String(courierId) ||
                        String(cs._id) === String(courierId)
                ) || null
            );
        }, [courierList, focusedTarget, focusedOrderPin]);

        const isFocusMode =
            focusedTarget?.type === "order"
                ? Boolean(focusedOrderPin)
                : focusedTarget?.type === "courier"
                ? Boolean(focusedCourier) || focusedCourierPins.length > 0
                : false;

        const courierFilteredSubCompanyIds = useMemo(() => {
            if (!courierSearchText.trim()) return null;
            const ids = orderPins.map(pin => pin.pickup?._id).filter(Boolean);
            return new Set(ids.map(String));
        }, [orderPins, courierSearchText]);

        const subCompanyList = useMemo(() => {
            let list = subCompanyMapList || [];
            if (poolSubCompanyIds) {
                list = list.filter(sc => poolSubCompanyIds.has(String(sc._id)));
            }
            if (filters.subCompany?.length) {
                list = list.filter(sc => filters.subCompany.includes(sc._id));
            }
            if (courierFilteredSubCompanyIds) {
                list = list.filter(sc => courierFilteredSubCompanyIds.has(String(sc._id)));
            }
            return list;
        }, [
            subCompanyMapList,
            filters.subCompany,
            poolSubCompanyIds,
            courierFilteredSubCompanyIds
        ]);

        const visibleCourierList = isFocusMode
            ? focusedCourier
                ? [focusedCourier]
                : []
            : courierList;
        const visibleSubCompanyList = isFocusMode ? focusedSubCompanies : subCompanyList;
        const visibleOrderPins = isFocusMode ? focusedPins.filter(Boolean) : orderPins;

        // Harita init
        useEffect(() => {
            if (mapRef.current && !mapInstanceRef.current) {
                configureLeafletMarkerIcons(L);
                const turkeyBounds = L.latLngBounds(
                    TURKEY_BOUNDS.southWest,
                    TURKEY_BOUNDS.northEast
                );
                mapInstanceRef.current = L.map(mapRef.current, {
                    maxBounds: turkeyBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: MIN_ZOOM,
                    maxZoom: 22
                }).setView([center.lat, center.lng], zoom);

                tileLayerRef.current = addTileLayerWithFallback(
                    L,
                    mapInstanceRef.current,
                    getTileLayerPreference()
                );

                courierLayerRef.current = L.layerGroup();
                subCompanyLayerRef.current = L.layerGroup();
                orderLayerRef.current = L.layerGroup();

                mapInstanceRef.current.on("zoomstart movestart", () => {
                    setSelectedCourier(null);
                    setSelectedSubCompany(null);
                    setSelectedOrder(null);
                });
            }
            return () => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.remove();
                    mapInstanceRef.current = null;
                }
            };
        }, []);

        // Şehir filtresi değiştiğinde haritayı ilgili şehir merkezine taşı.
        useEffect(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.setView(
                    [cityView.center.lat, cityView.center.lng],
                    cityView.zoom
                );
            }
        }, [cityView]);

        useEffect(() => {
            if (mapInstanceRef.current) {
                setTimeout(() => mapInstanceRef.current.invalidateSize(), 100);
            }
        }, [mapMode]);

        // Kapatma: dışarı tıklama
        useEffect(() => {
            const handleClickOutside = event => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    setSelectedCourier(null);
                    setSelectedSubCompany(null);
                    setSelectedOrder(null);
                }
            };
            if (selectedCourier || selectedSubCompany || selectedOrder) {
                document.addEventListener("mousedown", handleClickOutside);
            }
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, [selectedCourier, selectedSubCompany, selectedOrder]);

        const openPopupAt = (e, setter, value) => {
            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(e.latlng);
            setPopupPosition({x: containerPoint.x, y: containerPoint.y});
            setSelectedCourier(null);
            setSelectedSubCompany(null);
            setSelectedOrder(null);
            setter(value);
        };

        // Odak rozetindeki sipariş numarasına tıklanınca panoya kopyalanır; tik ikonu 2 sn
        // görünüp kaybolur. `navigator.clipboard` yalnızca güvenli bağlamda (https/localhost)
        // çalışır; `execCommand` fallback'i (WhatsappSupportButton'daki kalıp) diğer durumları
        // kapsar.
        const handleCopyOrderNumber = orderNumber => {
            const onCopied = () => {
                // Kopyalanma zaten sipariş numarasının yanındaki tikle gösteriliyor; mesajda
                // ikinci bir tik ikonuna gerek yok — `icon` boş verilerek varsayılanı kapatılır.
                message.open({
                    content: `${orderNumber} kopyalandı`,
                    type: "success",
                    icon: <span />
                });
                setOrderNumberCopied(true);
                setTimeout(() => setOrderNumberCopied(false), 2000);
            };
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(orderNumber).then(onCopied);
                } else {
                    const el = document.createElement("textarea");
                    el.value = orderNumber;
                    el.style.position = "fixed";
                    el.style.opacity = "0";
                    document.body.appendChild(el);
                    el.focus();
                    el.select();
                    document.execCommand("copy");
                    document.body.removeChild(el);
                    onCopied();
                }
            } catch (e) {
                message.error("Sipariş numarası kopyalanamadı");
            }
        };

        // Popup varsayılan olarak pin'in altına açılır; harita alanının altına taşıyorsa pin'in
        // üstüne çevrilir, yatayda da harita sınırlarına sıkıştırılır. Aksi halde kenardaki
        // pin'lerde kart görünür alanın dışında kalıyordu. Ölçüm MapDiv'e göre yapılır —
        // popupPosition zaten latLngToContainerPoint ile aynı koordinat sisteminde üretiliyor.
        useLayoutEffect(() => {
            const popupEl = popupRef.current;
            const mapEl = mapRef.current;
            if (!popupEl || !mapEl) return undefined;

            const reposition = () => {
                const popupWidth = popupEl.offsetWidth;
                const popupHeight = popupEl.offsetHeight;
                const mapWidth = mapEl.clientWidth;
                const mapHeight = mapEl.clientHeight;

                const maxLeft = Math.max(
                    POPUP_EDGE_MARGIN,
                    mapWidth - popupWidth - POPUP_EDGE_MARGIN
                );
                const left = Math.min(
                    Math.max(popupPosition.x - popupWidth / 2, POPUP_EDGE_MARGIN),
                    maxLeft
                );

                const belowTop = popupPosition.y + POPUP_PIN_OFFSET;
                const fitsBelow = belowTop + popupHeight + POPUP_EDGE_MARGIN <= mapHeight;
                const maxTop = Math.max(
                    POPUP_EDGE_MARGIN,
                    mapHeight - popupHeight - POPUP_EDGE_MARGIN
                );
                const top = fitsBelow
                    ? belowTop
                    : Math.min(
                          Math.max(
                              popupPosition.y - POPUP_PIN_OFFSET - popupHeight,
                              POPUP_EDGE_MARGIN
                          ),
                          maxTop
                      );

                setPopupOffset(prev =>
                    prev.left === left && prev.top === top ? prev : {left, top}
                );
            };

            reposition();

            if (typeof ResizeObserver === "undefined") return undefined;
            // Kart içeriği (kurye vardiyası, şube telefonu) async geliyor — yükseklik değişince
            // yeniden konumlandır.
            const observer = new ResizeObserver(reposition);
            observer.observe(popupEl);
            return () => observer.disconnect();
        }, [selectedCourier, selectedSubCompany, selectedOrder, popupPosition]);

        // Kurye marker'ları
        useEffect(() => {
            const layer = courierLayerRef.current;
            if (!layer) return;
            layer.clearLayers();
            visibleCourierList.forEach(courier => {
                if (!courier?.location?.coordinates) return;
                const icon = buildCircleIcon({
                    html: renderColoredIcon(
                        courierStatusColors[courier.status] || "#d9d9d9",
                        <CourierIcon courierType={courier.type || "motorCourier"} />
                    )
                });
                const marker = L.marker(
                    [courier.location.coordinates[1], courier.location.coordinates[0]],
                    {icon}
                );
                marker.on("click", e => openPopupAt(e, setSelectedCourier, courier));
                layer.addLayer(marker);
            });
        }, [visibleCourierList]);

        // Alt firma marker'ları
        useEffect(() => {
            const layer = subCompanyLayerRef.current;
            if (!layer) return;
            layer.clearLayers();
            visibleSubCompanyList.forEach(subCompany => {
                if (!subCompany?.location?.latitude || !subCompany?.location?.longitude) return;
                const icon = buildCircleIcon({
                    html: SubcompanyMarker({subcompany: subCompany, isPoolCustomer: false})
                });
                const marker = L.marker(
                    [subCompany.location.latitude, subCompany.location.longitude],
                    {
                        icon
                    }
                );
                marker.on("click", e => openPopupAt(e, setSelectedSubCompany, subCompany));
                layer.addLayer(marker);
            });
        }, [visibleSubCompanyList]);

        // Sipariş marker'ları
        useEffect(() => {
            const layer = orderLayerRef.current;
            if (!layer) return;
            layer.clearLayers();
            visibleOrderPins.forEach(pin => {
                const icon = buildCircleIcon({
                    html: renderColoredIcon(pickupColor, <InboxOutlined style={{color: "#fff"}} />)
                });
                const marker = L.marker([pin.lat, pin.lng], {icon});
                marker.on("click", e => openPopupAt(e, setSelectedOrder, pin));
                layer.addLayer(marker);
            });
        }, [visibleOrderPins]);

        // Filtreye göre görünür olması gereken tüm pinlerin koordinatları — hem filtre tabanlı
        // fitBounds hem de odak kapanışındaki fitBounds aynı kapsamı kullanır.
        const collectVisiblePoints = () => {
            const points = [];
            if (options.showCouriers) {
                courierList.forEach(courier => {
                    if (courier?.location?.coordinates) {
                        points.push([
                            courier.location.coordinates[1],
                            courier.location.coordinates[0]
                        ]);
                    }
                });
            }
            if (options.showSubCompanies) {
                subCompanyList.forEach(subCompany => {
                    if (subCompany?.location?.latitude && subCompany?.location?.longitude) {
                        points.push([subCompany.location.latitude, subCompany.location.longitude]);
                    }
                });
            }
            if (options.showOrders) {
                orderPins.forEach(pin => points.push([pin.lat, pin.lng]));
            }
            return points;
        };

        // fitBounds yalnızca kullanıcı bir filtre ya da katman switch'i değiştirdiğinde çalışır —
        // polling (20 sn harita, 30 sn tablo) veriyi tazelediğinde kullanıcının pan/zoom'u korunur.
        // Yalnızca switch'i açık olan katmanların pinleri hesaba katılır. Hiç pin yoksa (ör.
        // filtreler hepsini eledi) mevcut görünüm korunur ve imza tüketilmez; veri sonradan
        // geldiğinde fit yine yapılır. `maxZoom` tek/az sayıda pin varken absürt yakın zoom'u
        // önlüyor.
        useEffect(() => {
            const map = mapInstanceRef.current;
            if (!map) return;
            // Haritayı besleyen üç kaynak (kurye / şube / sipariş) farklı anlarda dönüyor.
            // Beklenmeseydi ilk gelen kaynağın pinlerine (ör. yalnızca kuryelere) oturulur,
            // imza tüketildiği için kalan pinler geldiğinde bir daha fit edilmezdi.
            if (showLoadingOverlay) return;

            const signature = [
                filterSignature,
                options.showCouriers,
                options.showSubCompanies,
                options.showOrders
            ].join("|");
            if (lastFitSignatureRef.current === signature) return;

            const points = collectVisiblePoints();

            if (!points.length) return;
            lastFitSignatureRef.current = signature;
            // Şehir değişiminde harita önce şehir merkezine setView ile taşınıyor; bu animasyon
            // hâlâ sürerken yapılan fitBounds animasyon tamamlanınca eziliyordu. stop() devam
            // eden pan/zoom animasyonunu iptal eder.
            map.stop();
            // fitBounds, haritanın kendi minZoom'unu (MIN_ZOOM) zaten aşamaz — ayrıca bir
            // sınırlama gerekmiyor.
            map.fitBounds(L.latLngBounds(points), {padding: [40, 40], maxZoom: 15});
        }, [
            filterSignature,
            showLoadingOverlay,
            courierList,
            subCompanyList,
            orderPins,
            options.showCouriers,
            options.showSubCompanies,
            options.showOrders
        ]);

        // Switch'lere göre layer ekle/çıkar (marker'lar yeniden üretilmez). Odak modunda
        // switch'ler yok sayılır — odaktaki üç pin her zaman gösterilir, kullanıcının katman
        // tercihi (`options`) store'da hiç değişmeden korunur.
        useEffect(() => {
            const map = mapInstanceRef.current;
            const layer = courierLayerRef.current;
            if (!map || !layer) return;
            if (options.showCouriers || isFocusMode) layer.addTo(map);
            else map.removeLayer(layer);
        }, [options.showCouriers, isFocusMode, courierList]);

        useEffect(() => {
            const map = mapInstanceRef.current;
            const layer = subCompanyLayerRef.current;
            if (!map || !layer) return;
            if (options.showSubCompanies || isFocusMode) layer.addTo(map);
            else map.removeLayer(layer);
        }, [options.showSubCompanies, isFocusMode, subCompanyList]);

        useEffect(() => {
            const map = mapInstanceRef.current;
            const layer = orderLayerRef.current;
            if (!map || !layer) return;
            if (options.showOrders || isFocusMode) layer.addTo(map);
            else map.removeLayer(layer);
        }, [options.showOrders, isFocusMode, orderPins]);

        // Odak zoom'u, filtre tabanlı fitBounds'tan ayrı tutulur: imzası sipariş anahtarıdır,
        // böylece 20/30 sn'lik polling tazelemesi (yeni dizi referansı) yeniden zoom yapmaz.
        useEffect(() => {
            const map = mapInstanceRef.current;
            if (!map) return;

            if (!isFocusMode) {
                const wasFocused = lastFocusKeyRef.current !== null;
                lastFocusKeyRef.current = null;
                // Temizle (ya da odaktaki sipariş listeden düşme) önceki pan/zoom'u geri yüklemez;
                // filtreye uyan tüm pinlere göre normal fitBounds gibi yeniden zoom yapılır.
                if (wasFocused) {
                    const points = collectVisiblePoints();
                    if (points.length) {
                        lastFitSignatureRef.current = [
                            filterSignature,
                            options.showCouriers,
                            options.showSubCompanies,
                            options.showOrders
                        ].join("|");
                        map.stop();
                        map.fitBounds(L.latLngBounds(points), {padding: [40, 40], maxZoom: 15});
                    }
                }
                return;
            }

            const focusKey =
                focusedTarget.type === "courier"
                    ? `courier-${focusedTarget.courierId}`
                    : `${focusedTarget.deliveryId}-${focusedTarget.orderId || ""}`;
            if (lastFocusKeyRef.current === focusKey) return;

            const points = focusedPins.filter(Boolean).map(pin => [pin.lat, pin.lng]);
            focusedSubCompanies.forEach(sc => {
                if (sc?.location?.latitude && sc?.location?.longitude) {
                    points.push([sc.location.latitude, sc.location.longitude]);
                }
            });
            if (focusedCourier?.location?.coordinates) {
                points.push([
                    focusedCourier.location.coordinates[1],
                    focusedCourier.location.coordinates[0]
                ]);
            }
            if (!points.length) return;

            lastFocusKeyRef.current = focusKey;
            map.stop();
            map.fitBounds(L.latLngBounds(points), {padding: [60, 60], maxZoom: 16});
        }, [
            isFocusMode,
            focusedTarget,
            focusedPins,
            focusedSubCompanies,
            focusedCourier,
            filterSignature,
            options.showCouriers,
            options.showSubCompanies,
            options.showOrders,
            courierList,
            subCompanyList,
            orderPins
        ]);

        // Polling'de sipariş teslim edilip/iptal olup ya da kuryenin son siparişi düşüp odak
        // boşa çıkarsa, rozet de zoom da asılı kalmamalı. Yükleme sürerken tetiklenmemeli —
        // veri bir an boş olabiliyor.
        useEffect(() => {
            if (focusedTarget && !isFocusMode && !showLoadingOverlay) onClearFocus();
        }, [focusedTarget, isFocusMode, showLoadingOverlay, onClearFocus]);

        // Farklı bir hedefe geçilince ya da odak kapanınca önceki siparişin tik göstergesi
        // asılı kalmamalı.
        useEffect(() => {
            setOrderNumberCopied(false);
        }, [focusedTarget?.key]);

        // Kurye pop-up'ı için vardiya detayını çapraz getir (maplist'te yok)
        useEffect(() => {
            if (!selectedCourier?.courier?._id) {
                setCourierInfo(null);
                return;
            }
            let cancelled = false;
            setCourierInfoLoading(true);
            stores.courierStore
                .getCourierInfo({courierId: selectedCourier.courier._id})
                .then(result => {
                    if (!cancelled) {
                        setCourierInfo(result || null);
                        setCourierInfoLoading(false);
                    }
                });
            return () => {
                cancelled = true;
            };
        }, [selectedCourier?.courier?._id]);

        // Alt firma telefonu (hem alt firma pini hem sipariş pini için) — cache'li
        const fetchSubCompanyDetail = subCompanyId => {
            if (!subCompanyId) return;
            if (subCompanyDetailCache.current.has(subCompanyId)) {
                setSubCompanyDetail(subCompanyDetailCache.current.get(subCompanyId));
                return;
            }
            setSubCompanyDetailLoading(true);
            stores.companyStore.getSubCompany({subCompanyId}).then(res => {
                const detail = res?.data;
                subCompanyDetailCache.current.set(subCompanyId, detail);
                setSubCompanyDetail(detail);
                setSubCompanyDetailLoading(false);
            });
        };

        useEffect(() => {
            if (selectedSubCompany?._id) fetchSubCompanyDetail(selectedSubCompany._id);
            else if (selectedOrder?.pickup?._id) fetchSubCompanyDetail(selectedOrder.pickup._id);
            else setSubCompanyDetail(null);
        }, [selectedSubCompany?._id, selectedOrder?.pickup?._id]);

        const toggleMapMode = () => setMapMode(prev => (prev === "fixed" ? "relative" : "fixed"));

        useEffect(() => {
            const handleEsc = event => {
                if (event.keyCode === 27 && mapMode === "fixed") setMapMode("relative");
            };
            window.addEventListener("keydown", handleEsc);
            return () => window.removeEventListener("keydown", handleEsc);
        }, [mapMode]);

        return (
            <MapOuter mode={mapMode}>
                <MapDiv ref={mapRef} />
                {showLoadingOverlay && (
                    <MapLoadingOverlay>
                        <Spin />
                    </MapLoadingOverlay>
                )}
                {focusedTarget && (
                    <FocusBadge data-testid="liveOperationMapFocusBadge">
                        <EnvironmentOutlined />
                        {focusedTarget.type === "courier" ? (
                            <FocusOrderNo data-testid="liveOperationMapFocusCourierName">
                                {focusedTarget.courierName}
                            </FocusOrderNo>
                        ) : (
                            <ConditionalTooltip
                                condition
                                text="Kopyala"
                                child={
                                    <FocusOrderNo
                                        onClick={() =>
                                            handleCopyOrderNumber(focusedTarget.orderNumber)
                                        }
                                        data-testid="liveOperationMapFocusOrderNo"
                                    >
                                        {focusedTarget.orderNumber}
                                        {orderNumberCopied && (
                                            <FocusOrderNoCopyTick>
                                                <CheckOutlined />
                                            </FocusOrderNoCopyTick>
                                        )}
                                    </FocusOrderNo>
                                }
                            />
                        )}
                        <FocusClearButton
                            onClick={onClearFocus}
                            data-testid="liveOperationMapFocusClear"
                        >
                            <CloseOutlined /> Temizle
                        </FocusClearButton>
                    </FocusBadge>
                )}
                <ExpandButton onClick={toggleMapMode} data-testid="liveOperationMapExpand">
                    {mapMode === "relative" ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                </ExpandButton>
                <div style={{position: "absolute", right: "10px", top: "50px", zIndex: 1000}}>
                    <TileLayerControl
                        mapInstance={mapInstanceRef.current}
                        tileLayerRef={tileLayerRef}
                    />
                </div>
                <LiveOperationMapLayerSwitches options={options} onChange={onOptionsChange} />
                <CourierStatsBox stats={courierStats} />

                {selectedCourier && (
                    <ExternalPopup ref={popupRef} $left={popupOffset.left} $top={popupOffset.top}>
                        <PopupContent>
                            <CloseButton onClick={() => setSelectedCourier(null)}>×</CloseButton>
                            <MapCourierPopup
                                courier={selectedCourier}
                                courierInfo={courierInfo}
                                courierInfoLoading={courierInfoLoading}
                            />
                        </PopupContent>
                    </ExternalPopup>
                )}

                {selectedSubCompany && (
                    <ExternalPopup ref={popupRef} $left={popupOffset.left} $top={popupOffset.top}>
                        <PopupContent>
                            <CloseButton onClick={() => setSelectedSubCompany(null)}>×</CloseButton>
                            <MapSubCompanyPopup
                                subCompany={selectedSubCompany}
                                detail={subCompanyDetail}
                                detailLoading={subCompanyDetailLoading}
                            />
                        </PopupContent>
                    </ExternalPopup>
                )}

                {selectedOrder && (
                    <ExternalPopup ref={popupRef} $left={popupOffset.left} $top={popupOffset.top}>
                        <PopupContent>
                            <CloseButton onClick={() => setSelectedOrder(null)}>×</CloseButton>
                            <MapOrderPopup
                                order={selectedOrder.order}
                                delivery={selectedOrder.delivery}
                                subCompanyDetail={subCompanyDetail}
                                subCompanyDetailLoading={subCompanyDetailLoading}
                            />
                        </PopupContent>
                    </ExternalPopup>
                )}
            </MapOuter>
        );
    }
);
